import { Link, useLocation } from "react-router-dom";
import { Star, MapPin, Sparkles, Zap } from "lucide-react";

/**
 * HotelCard — the SwaanStay signature card. Premium black-and-gold treatment
 * with hover lift, large photo, price highlight, and Negotiate CTA badge.
 * Rendered against light or dark surfaces (light is default; add
 * `variant="dark"` for placement on the hero / featured strip).
 */
export default function HotelCard({ hotel, variant = "light" }) {
  const photo = hotel.photos?.[0] || "https://images.pexels.com/photos/34496702/pexels-photo-34496702.jpeg";
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const forward = new URLSearchParams();
  ["checkin", "checkout", "rooms", "guests"].forEach((k) => {
    const v = search.get(k);
    if (v) forward.set(k, v);
  });
  const qs = forward.toString();
  const to = `/hotels/${hotel.slug || hotel.hotel_id}${qs ? `?${qs}` : ""}`;

  const isDark = variant === "dark";
  const shellClass = isDark
    ? "ss-card ss-hover-lift block relative overflow-hidden border border-white/10 bg-[#1A1D24]"
    : "block relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-[#C8A34D] transition-all hover:shadow-[0_20px_60px_-20px_rgba(15,17,21,0.35)] hover:-translate-y-1 duration-300";

  const nameClass = isDark ? "text-white" : "text-slate-900";
  const cityClass = isDark ? "text-white/60" : "text-slate-500";
  const priceClass = isDark ? "text-white" : "text-slate-900";
  const labelClass = isDark ? "text-white/50" : "text-slate-400";

  // Compute a mock 'AI best offer' — 15% off listed to visually demonstrate the value prop.
  // (This is purely visual: the real quote comes from the negotiation flow.)
  const base = hotel.base_price || 0;
  const aiOffer = base ? Math.max(base - Math.round(base * 0.15), Math.round(base * 0.6)) : null;

  return (
    <Link
      to={to}
      data-testid={`hotel-card-${hotel.hotel_id}`}
      className={shellClass}
    >
      {/* Photo */}
      <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
        <img
          src={photo}
          alt={hotel.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />
        {/* AI Negotiable badge (top-right) */}
        <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-[#E8D8A8] border border-white/10">
          <Sparkles className="w-3 h-3" /> AI Negotiable
        </span>
        {/* Star rating (bottom-left) */}
        {hotel.rating != null && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-slate-900 text-xs font-bold">
            <Star className="w-3 h-3 fill-[#C8A34D] stroke-[#C8A34D]" /> {hotel.rating?.toFixed(1)}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        <h3 className={`font-head font-bold text-lg leading-tight line-clamp-1 ${nameClass}`}>
          {hotel.name}
        </h3>
        <div className={`mt-1 flex items-center gap-1 text-sm ${cityClass}`}>
          <MapPin className="w-3.5 h-3.5" /> {hotel.city}
        </div>

        {/* Amenities strip (up to 3) */}
        {hotel.amenities?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {hotel.amenities.slice(0, 3).map((a) => (
              <span
                key={a}
                className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${
                  isDark ? "bg-white/5 text-white/70 border border-white/10" : "bg-slate-100 text-slate-600"
                }`}
              >{a}</span>
            ))}
          </div>
        )}

        {/* Price */}
        <div className="mt-5 flex items-end justify-between">
          <div>
            <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${labelClass}`}>From</div>
            {aiOffer && aiOffer < base && (
              <div className="text-xs text-slate-400 line-through leading-none mt-1">₹{base.toLocaleString("en-IN")}</div>
            )}
            <div className={`font-head text-2xl font-black leading-none mt-1 ${priceClass}`}>
              ₹{(aiOffer || base)?.toLocaleString("en-IN")}
              <span className={`text-xs font-normal ml-1 ${cityClass}`}>/night</span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full ss-gold-gradient text-[11px] font-bold uppercase tracking-wider">
            <Zap className="w-3.5 h-3.5" /> Negotiate
          </span>
        </div>
      </div>
    </Link>
  );
}
